// Advertiser isolation guard for campaign lists rendered in the UI.
//
// Fail-closed: campaign rows are only rendered when they can be attributed to
// the currently selected advertiser. A response addressed to a different
// advertiser (stale cache, race after advertiser switch, backend mix-up)
// drops the entire list; individual rows carrying a mismatched advertiser_id
// are excluded; rows missing advertiser attribution are excluded wherever the
// source is expected to provide it (orphaned campaigns). Every exclusion is
// reported through onViolation so isolation breaks are logged, never silent.

function normalizeId(value) {
  return String(value ?? "").trim();
}

export function enforceCampaignAdvertiserIsolation({
  rows = [],
  expectedAdvertiserId,
  responseAdvertiserId = null,
  requireRowAdvertiser = false,
  source = "",
  onViolation = null,
} = {}) {
  const violations = [];
  const reportViolation = (violation) => {
    violations.push(violation);
    try {
      onViolation?.(violation);
    } catch (_e) {
      // logging must never break rendering
    }
  };

  const safeInput = Array.isArray(rows) ? rows : [];
  const expected = normalizeId(expectedAdvertiserId);

  if (!expected) {
    if (safeInput.length) {
      reportViolation({
        code: "missing_expected_advertiser",
        source,
        droppedCount: safeInput.length,
      });
    }
    return { rows: [], violations };
  }

  const echoed = normalizeId(responseAdvertiserId);
  if (echoed && echoed !== expected) {
    reportViolation({
      code: "response_advertiser_mismatch",
      source,
      expectedAdvertiserId: expected,
      responseAdvertiserId: echoed,
      droppedCount: safeInput.length,
    });
    return { rows: [], violations };
  }

  const safeRows = [];
  safeInput.forEach((row) => {
    const rowAdvertiser = normalizeId(row?.advertiser_id ?? row?.advertiserId);
    if (!rowAdvertiser) {
      if (requireRowAdvertiser) {
        reportViolation({
          code: "orphaned_campaign_excluded",
          source,
          campaignId: row?.id ?? row?.campaign_id ?? null,
          campaignName: row?.campaign_name ?? row?.name ?? null,
        });
        return;
      }
      safeRows.push(row);
      return;
    }
    if (rowAdvertiser !== expected) {
      reportViolation({
        code: "row_advertiser_mismatch",
        source,
        expectedAdvertiserId: expected,
        rowAdvertiserId: rowAdvertiser,
        campaignId: row?.id ?? row?.campaign_id ?? null,
        campaignName: row?.campaign_name ?? row?.name ?? null,
      });
      return;
    }
    safeRows.push(row);
  });

  return { rows: safeRows, violations };
}
